import { clients } from '../clients/grpcClients';
import {
  CheckoutItem,
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  SessionStatusRequest,
  SessionStatusResponse,
  StripeMetadata, // Import the metadata message
  BillingPortalSessionRequest,
  BillingPortalSessionResponse,
  GetSubscriptionRequest,
  GetSubscriptionByAccountRequest,
  Subscription,
  ListPricesRequest,
  ListPricesResponse,
  UpdateSeatsRequest,
  AddSubscriptionItemRequest,
  RemoveSubscriptionItemRequest,
  PauseSubscriptionRequest,
  ResumeSubscriptionRequest,
  CancelSubscriptionRequest,
  ReactivateSubscriptionRequest,
  PreviewSubscriptionUpdateRequest,
  PreviewSubscriptionUpdateResponse,
} from './protosCompiled/checkout/checkout_pb';

export class CheckoutService {
  public static createCheckoutSession = (
    items: CheckoutItem.AsObject[]
  ): Promise<CheckoutSessionResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new CheckoutSessionRequest();

      // Add each item to the request
      items.forEach(item => {
        const checkoutItem = new CheckoutItem();
        checkoutItem.setPriceId(item.priceId);
        checkoutItem.setQuantity(item.quantity);
        checkoutItem.setIstrial(item.istrial || false);

        // Set metadata if provided
        if (item.metadata) {
          const metadata = new StripeMetadata();
          metadata.setUbAccountId(item.metadata.ubAccountId);
          metadata.setNumSeats(item.metadata.numSeats);
          checkoutItem.setMetadata(metadata);
        }

        request.addItems(checkoutItem);
      });

      clients.checkoutServiceClient.createCheckoutSession(
        request,
        {}, // Pass an empty object for additional metadata if needed
        (err, response) => {
          if (err) {
            reject(err);
            return;
          }
          if (response) {
            resolve(response.toObject());
          } else {
            reject(new Error('No response received'));
          }
        }
      );
    });
  };

  public static retrieveCheckoutSession = (
    sessionId: string
  ): Promise<SessionStatusResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new SessionStatusRequest();
      request.setSessionId(sessionId);

      clients.checkoutServiceClient.retrieveCheckoutSession(
        request,
        {}, // Pass an empty object or null if additional metadata is required but not needed
        (err, response) => {
          if (err) {
            reject(err);
            return;
          }
          if (response) {
            resolve(response.toObject());
          } else {
            reject(new Error('No response received'));
          }
        }
      );
    });
  };

  public static createBillingPortalSession = (
    stripeCustomerId: string,
    returnUrl: string
  ): Promise<BillingPortalSessionResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new BillingPortalSessionRequest();
      request.setStripeCustomerId(stripeCustomerId);
      request.setReturnUrl(returnUrl);

      clients.checkoutServiceClient.createBillingPortalSession(request, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        if (response) {
          resolve(response.toObject());
        } else {
          reject(new Error('No response received'));
        }
      });
    });
  };

  public static getSubscription = (subscriptionId: string): Promise<Subscription.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new GetSubscriptionRequest();
      request.setSubscriptionId(subscriptionId);
      clients.checkoutServiceClient.getSubscription(request, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        if (response) {
          resolve(response.toObject());
        } else {
          reject(new Error('No response received'));
        }
      });
    });
  };

  public static getSubscriptionByAccount = (
    ubAccountId: string
  ): Promise<Subscription.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new GetSubscriptionByAccountRequest();
      request.setUbAccountId(ubAccountId);
      clients.checkoutServiceClient.getSubscriptionByAccount(request, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        if (response) {
          resolve(response.toObject());
        } else {
          reject(new Error('No response received'));
        }
      });
    });
  };

  public static listPrices = (
    productFilter: string[] = [],
    currency?: string
  ): Promise<ListPricesResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new ListPricesRequest();
      if (productFilter?.length) {
        request.setProductFilterList(productFilter);
      }
      if (currency) {
        request.setCurrency(currency);
      }
      clients.checkoutServiceClient.listPrices(request, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        if (response) {
          resolve(response.toObject());
        } else {
          reject(new Error('No response received'));
        }
      });
    });
  };

  public static updateSeats = (params: {
    subscriptionId: string;
    subscriptionItemId: string;
    quantity: number;
    prorationBehavior?: string;
  }): Promise<Subscription.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new UpdateSeatsRequest();
      request.setSubscriptionId(params.subscriptionId);
      request.setSubscriptionItemId(params.subscriptionItemId);
      request.setQuantity(params.quantity);
      if (params.prorationBehavior) {
        request.setProrationBehavior(params.prorationBehavior);
      }
      clients.checkoutServiceClient.updateSeats(request, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        if (response) {
          resolve(response.toObject());
        } else {
          reject(new Error('No response received'));
        }
      });
    });
  };

  public static addSubscriptionItem = (params: {
    subscriptionId: string;
    priceId: string;
    quantity: number;
    prorationBehavior?: string;
  }): Promise<Subscription.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new AddSubscriptionItemRequest();
      request.setSubscriptionId(params.subscriptionId);
      request.setPriceId(params.priceId);
      request.setQuantity(params.quantity);
      if (params.prorationBehavior) {
        request.setProrationBehavior(params.prorationBehavior);
      }
      clients.checkoutServiceClient.addSubscriptionItem(request, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        if (response) {
          resolve(response.toObject());
        } else {
          reject(new Error('No response received'));
        }
      });
    });
  };

  public static removeSubscriptionItem = (params: {
    subscriptionId: string;
    subscriptionItemId: string;
    prorationBehavior?: string;
  }): Promise<Subscription.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new RemoveSubscriptionItemRequest();
      request.setSubscriptionId(params.subscriptionId);
      request.setSubscriptionItemId(params.subscriptionItemId);
      if (params.prorationBehavior) {
        request.setProrationBehavior(params.prorationBehavior);
      }
      clients.checkoutServiceClient.removeSubscriptionItem(request, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        if (response) {
          resolve(response.toObject());
        } else {
          reject(new Error('No response received'));
        }
      });
    });
  };

  public static pauseSubscription = (params: {
    subscriptionId: string;
    behavior: string;
  }): Promise<Subscription.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new PauseSubscriptionRequest();
      request.setSubscriptionId(params.subscriptionId);
      request.setBehavior(params.behavior);
      clients.checkoutServiceClient.pauseSubscription(request, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        if (response) {
          resolve(response.toObject());
        } else {
          reject(new Error('No response received'));
        }
      });
    });
  };

  public static resumeSubscription = (subscriptionId: string): Promise<Subscription.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new ResumeSubscriptionRequest();
      request.setSubscriptionId(subscriptionId);
      clients.checkoutServiceClient.resumeSubscription(request, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        if (response) {
          resolve(response.toObject());
        } else {
          reject(new Error('No response received'));
        }
      });
    });
  };

  public static cancelSubscription = (params: {
    subscriptionId: string;
    atPeriodEnd?: boolean;
  }): Promise<Subscription.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new CancelSubscriptionRequest();
      request.setSubscriptionId(params.subscriptionId);
      if (typeof params.atPeriodEnd === 'boolean') {
        request.setAtPeriodEnd(params.atPeriodEnd);
      }
      clients.checkoutServiceClient.cancelSubscription(request, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        if (response) {
          resolve(response.toObject());
        } else {
          reject(new Error('No response received'));
        }
      });
    });
  };

  public static reactivateSubscription = (
    subscriptionId: string
  ): Promise<Subscription.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new ReactivateSubscriptionRequest();
      request.setSubscriptionId(subscriptionId);
      clients.checkoutServiceClient.reactivateSubscription(request, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        if (response) {
          resolve(response.toObject());
        } else {
          reject(new Error('No response received'));
        }
      });
    });
  };

  public static previewSubscriptionUpdate = (params: {
    subscriptionId: string;
    subscriptionItemId?: string;
    newQuantity?: number;
    addPriceId?: string;
    addQuantity?: number;
    removeSubscriptionItemId?: string;
    prorationBehavior?: string;
  }): Promise<PreviewSubscriptionUpdateResponse.AsObject> => {
    return new Promise((resolve, reject) => {
      const request = new PreviewSubscriptionUpdateRequest();
      request.setSubscriptionId(params.subscriptionId);
      if (params.subscriptionItemId) request.setSubscriptionItemId(params.subscriptionItemId);
      if (typeof params.newQuantity === 'number') request.setNewQuantity(params.newQuantity);
      if (params.addPriceId) request.setAddPriceId(params.addPriceId);
      if (typeof params.addQuantity === 'number') request.setAddQuantity(params.addQuantity);
      if (params.removeSubscriptionItemId)
        request.setRemoveSubscriptionItemId(params.removeSubscriptionItemId);
      if (params.prorationBehavior) request.setProrationBehavior(params.prorationBehavior);
      clients.checkoutServiceClient.previewSubscriptionUpdate(request, {}, (err, response) => {
        if (err) {
          reject(err);
          return;
        }
        if (response) {
          resolve(response.toObject());
        } else {
          reject(new Error('No response received'));
        }
      });
    });
  };
}
